/**
 * SURGICAL cell patching for SDMS workbooks — replaces ONLY the changed `<c>`
 * elements inside `xl/worksheets/sheetN.xml` and rewrites just that zip entry.
 *
 * WHY THIS EXISTS (client 2026-08-19, "the sheet is blank in ALPACA after I edit
 * it in Intelligence"). The save used to round-trip the whole workbook through
 * ExcelJS — `wb.xlsx.load(buf)` … `wb.xlsx.writeBuffer()`. DevExpress REFUSES to
 * open the worksheet XML ExcelJS emits: `Workbook.LoadDocument` returns **false**
 * and the control falls back to a blank default document, which is why LIMS
 * showed a single empty `Sheet1` while our own reader was perfectly happy.
 *
 * Proven with the real DevExpress v20.1 engine, swapping one ExcelJS-written part
 * at a time into a known-good workbook:
 *
 *     original                              -> loaded=True   sheets=3
 *     original + ExcelJS xl/workbook.xml    -> loaded=True   sheets=3
 *     original + ExcelJS xl/styles.xml      -> loaded=True   sheets=3
 *     original + ExcelJS worksheets/sheet1  -> loaded=FALSE  sheets=1   <-- the culprit
 *
 * A plain load→write with ZERO edits reproduces it, so the trigger is ExcelJS's
 * serializer, not our edits. Two plausible-looking causes were tested and
 * disproved on the way: the bare zip directory entries ExcelJS adds, and the
 * dropped `localSheetId` on `_xlnm._FilterDatabase` — restoring each individually
 * still failed to load.
 *
 * So: never re-serialize. Every other byte of the package stays exactly as
 * DevExpress wrote it. Re-zipping itself is safe — the hybrids above were rebuilt
 * zips and loaded fine.
 */
import JSZip from 'jszip';

/** One edited cell. `row`/`col` are 0-based, matching Univer's model. */
export interface SheetCellEdit {
  row: number;
  col: number;
  value: unknown;
}

/** 0-based (row, col) → an A1 reference. */
export function a1(row0: number, col0: number): string {
  let n = col0 + 1;
  let s = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = (n - 1 - m) / 26;
  }
  return `${s}${row0 + 1}`;
}

/** A1 column letters → 0-based index. */
function colOf(ref: string): number {
  const m = /^([A-Z]+)/.exec(ref);
  if (!m) return -1;
  let n = 0;
  for (const ch of m[1]!) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Build the replacement `<c>`. The existing STYLE attribute is carried over so
 * the cell keeps its formatting; the type is re-derived from the new value.
 * A numeric value is written bare (`<v>`), text as an inline string — never as a
 * shared string, because appending to `sharedStrings.xml` would mean rewriting a
 * second part and renumbering every index in the sheet.
 */
function buildCell(ref: string, styleAttr: string, value: unknown): string {
  const raw = value == null ? '' : String(value);
  const trimmed = raw.trim();
  if (trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed))
    return `<c r="${ref}"${styleAttr}><v>${trimmed}</v></c>`;
  if (trimmed === '') return `<c r="${ref}"${styleAttr}/>`;
  return `<c r="${ref}"${styleAttr} t="inlineStr"><is><t xml:space="preserve">${esc(raw)}</t></is></c>`;
}

/** Apply one cell edit to a worksheet XML string. Returns the new XML. */
export function patchCell(xml: string, ref: string, value: unknown): string {
  // 1. The cell already exists — replace it in place, keeping its style.
  const cellRe = new RegExp(`<c r="${ref}"([^>]*?)(?:/>|>[\\s\\S]*?</c>)`);
  const hit = cellRe.exec(xml);
  if (hit) {
    const styleMatch = / s="\d+"/.exec(hit[1] ?? '');
    return xml.slice(0, hit.index) + buildCell(ref, styleMatch?.[0] ?? '', value)
      + xml.slice(hit.index + hit[0].length);
  }

  // 2. The cell is absent but its ROW exists — insert in column order.
  const rowNum = ref.replace(/^[A-Z]+/, '');
  const col = colOf(ref);
  const rowRe = new RegExp(`<row r="${rowNum}"([^>]*?)(?:/>|>([\\s\\S]*?)</row>)`);
  const rowHit = rowRe.exec(xml);
  if (rowHit) {
    const inner = rowHit[2] ?? '';
    const cells = [...inner.matchAll(/<c r="([A-Z]+\d+)"/g)];
    const before = cells.find(c => colOf(c[1]!) > col);
    const newCell = buildCell(ref, '', value);
    const newInner = before
      ? inner.slice(0, before.index) + newCell + inner.slice(before.index)
      : inner + newCell;
    const rebuilt = `<row r="${rowNum}"${rowHit[1] ?? ''}>${newInner}</row>`;
    return xml.slice(0, rowHit.index) + rebuilt + xml.slice(rowHit.index + rowHit[0].length);
  }

  // 3. Neither exists — append a row inside <sheetData>, in row order.
  const newRow = `<row r="${rowNum}">${buildCell(ref, '', value)}</row>`;
  const rows = [...xml.matchAll(/<row r="(\d+)"/g)];
  const after = rows.find(r => parseInt(r[1]!, 10) > parseInt(rowNum, 10));
  if (after) return xml.slice(0, after.index) + newRow + xml.slice(after.index);
  const close = xml.lastIndexOf('</sheetData>');
  if (close < 0) return xml;                       // not a worksheet part — leave untouched
  return xml.slice(0, close) + newRow + xml.slice(close);
}

/**
 * Refresh a FORMULA cell's cached value, keeping its `<f>` untouched.
 *
 * DevExpress renders the CACHED value and does not recalculate on load — proven:
 * `<calcPr fullCalcOnLoad="1"/>` makes no difference to it. So after an edit the
 * dependent formulas kept their old numbers in LIMS (edit F11 to 1.23 and G11
 * still read 104.6 for `=F11*20`) while Intelligence showed the recomputed value,
 * because Univer calculates live. Writing the recomputed value into `<v>` makes
 * the stored file self-consistent for any reader.
 *
 * A string result carries `t="str"`, which is what DevExpress itself writes for
 * one; a numeric result carries no type attribute.
 */
export function patchFormulaCache(xml: string, ref: string, value: unknown): string {
  const re = new RegExp(`<c r="${ref}"([^>]*?)>([\\s\\S]*?)</c>`);
  const hit = re.exec(xml);
  if (!hit) return xml;
  const inner = hit[2] ?? '';
  const fMatch = /<f[^>]*>[\s\S]*?<\/f>|<f[^>]*\/>/.exec(inner);
  if (!fMatch) return xml;                       // not a formula cell — never touch it
  const style = / s="\d+"/.exec(hit[1] ?? '')?.[0] ?? '';
  const raw = value == null ? '' : String(value);
  const trimmed = raw.trim();
  const isNum = trimmed !== '' && /^-?\d+(\.\d+)?$/.test(trimmed);
  const typeAttr = isNum ? '' : ' t="str"';
  const cached = isNum ? `<v>${trimmed}</v>` : trimmed === '' ? '<v/>' : `<v>${esc(raw)}</v>`;
  return xml.slice(0, hit.index)
    + `<c r="${ref}"${style}${typeAttr}>${fMatch[0]}${cached}</c>`
    + xml.slice(hit.index + hit[0].length);
}

/** Resolve each sheet NAME to its part path (`xl/worksheets/sheetN.xml`). */
async function sheetPaths(zip: JSZip): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const wbXml = await zip.file('xl/workbook.xml')?.async('string');
  const relXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
  if (!wbXml || !relXml) return out;

  const targets = new Map<string, string>();
  for (const m of relXml.matchAll(/<Relationship([^>]*)\/>/g)) {
    const attrs = m[1] ?? '';
    const id = /Id="([^"]+)"/.exec(attrs)?.[1];
    const target = /Target="([^"]+)"/.exec(attrs)?.[1];
    if (id && target) targets.set(id, target.replace(/^\/?xl\//, '').replace(/^\//, ''));
  }
  for (const m of wbXml.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const attrs = m[1] ?? '';
    const name = /name="([^"]*)"/.exec(attrs)?.[1];
    const rid = /r:id="([^"]+)"/.exec(attrs)?.[1];
    if (!name || !rid) continue;
    const t = targets.get(rid);
    if (t) out.set(name, `xl/${t}`);
  }
  return out;
}

/**
 * Patch the given cells into the workbook, touching nothing else.
 *
 * @param originalBuf the workbook exactly as it came from the server
 * @param editsBySheet sheet NAME → the cells to write
 * @returns a new .xlsx buffer, or the original untouched if nothing applied
 */
export async function patchWorkbookCells(
  originalBuf: ArrayBuffer,
  editsBySheet: Record<string, SheetCellEdit[]>,
  recalcBySheet: Record<string, SheetCellEdit[]> = {},
): Promise<ArrayBuffer> {
  const zip = await JSZip.loadAsync(originalBuf);
  const paths = await sheetPaths(zip);
  let touched = 0;

  const sheets = new Set([...Object.keys(editsBySheet), ...Object.keys(recalcBySheet)]);
  for (const sheetName of sheets) {
    const edits = editsBySheet[sheetName] ?? [];
    const recalc = recalcBySheet[sheetName] ?? [];
    if (!edits.length && !recalc.length) continue;
    const path = paths.get(sheetName);
    if (!path) continue;
    const file = zip.file(path);
    if (!file) continue;
    let xml = await file.async('string');
    for (const e of edits) {
      xml = patchCell(xml, a1(e.row, e.col), e.value);
      touched++;
    }
    // Dependent formulas keep their `<f>`; only the cached `<v>` is refreshed.
    for (const r of recalc) {
      xml = patchFormulaCache(xml, a1(r.row, r.col), r.value);
      touched++;
    }
    zip.file(path, xml, { createFolders: false });
  }

  if (touched === 0) return originalBuf;

  // DROP THE STALE CALC CHAIN — the last piece of the blank-sheet bug, and the
  // one that actually decides it. xl/calcChain.xml caches the evaluation order of
  // every FORMULA cell. Writing a literal over a formula (which is what an edit
  // does) leaves the chain pointing at a cell that no longer has one, and
  // DevExpress rejects the WHOLE workbook for it — measured on a surgically
  // patched file that was otherwise byte-identical to the original:
  //
  //     calcChain kept    -> LoadDocument=false, 1 empty sheet
  //     calcChain dropped -> LoadDocument=true,  3 sheets, edit present
  //
  // The chain is a rebuildable cache: Excel and DevExpress regenerate it on the
  // next calculation, so removing it is what Excel itself does. Its relationship
  // and content-type override go too, or the package references a missing part.
  const chain = 'xl/calcChain.xml';
  if (zip.file(chain)) {
    zip.remove(chain);
    const rels = await zip.file('xl/_rels/workbook.xml.rels')?.async('string');
    if (rels)
      zip.file('xl/_rels/workbook.xml.rels',
        rels.replace(/<Relationship[^>]*calcChain\.xml"[^>]*\/>/g, ''), { createFolders: false });
    const ct = await zip.file('[Content_Types].xml')?.async('string');
    if (ct)
      zip.file('[Content_Types].xml',
        ct.replace(/<Override[^>]*calcChain\.xml"[^>]*\/>/g, ''), { createFolders: false });
  }

  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE' }) as Promise<ArrayBuffer>;
}
